import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { WEEKDAYS_SHORT } from '../../format';

/**
 * The month laid out as weeks, the way a wall calendar is.
 *
 * Weeks start on Monday. That is not a preference: the working week here runs
 * Monday to Friday, and a Sunday-first grid splits every weekend across two
 * rows, so the two days nobody works stop reading as one block.
 *
 * The leading and trailing days belong to the neighbouring months and are kept
 * rather than blanked. A grid that stops at the 1st leaves a ragged first row
 * and, worse, hides that someone's absence runs across the boundary — which is
 * exactly the question this screen exists to answer. They are marked
 * `inMonth: false` so the view can dim them.
 */

export interface MonthGridDay {
  readonly date: CalendarDate;
  /** False for the neighbouring month's days that pad the first and last weeks. */
  readonly inMonth: boolean;
}

export interface MonthGridWeek {
  readonly key: string;
  readonly days: readonly MonthGridDay[];
}

/**
 * Domingo = 0 … Sábado = 6, que es el orden en que se pinta la rejilla.
 *
 * **Era lunes primero, y esta pantalla era la única.** El resto del portal —el
 * calendario del asistente, la tabla de feriados— usa `WEEKDAYS_SHORT`, que abre
 * en domingo porque así lo tiene el prototipo. Quien miraba «Calendario y
 * feriados» y luego pedía vacaciones veía la misma semana empezada en dos días
 * distintos, y contaba mal el fin de semana.
 *
 * `dayOfWeek` ya es domingo-primero —viene de `getUTCDay`— así que homologar era
 * dejar de corregirlo.
 */
export const sundayIndex = (date: CalendarDate): number => date.dayOfWeek;

/** Las mismas etiquetas que el resto del portal, no una copia con otro orden. */
export const WEEKDAY_LABELS: readonly string[] = [...WEEKDAYS_SHORT];

export const buildMonthGrid = (anchor: CalendarDate): readonly MonthGridWeek[] => {
  const first = anchor.startOfMonth;
  const last = anchor.endOfMonth;

  const start = first.addDays(-sundayIndex(first));
  const end = last.addDays(6 - sundayIndex(last));

  const weeks: MonthGridWeek[] = [];
  let cursor = start;

  while (cursor.isSameOrBefore(end)) {
    const key = cursor.toISO();
    const days: MonthGridDay[] = [];
    for (let i = 0; i < 7; i += 1) {
      days.push({
        date: cursor,
        inMonth: cursor.month === anchor.month && cursor.year === anchor.year,
      });
      cursor = cursor.addDays(1);
    }
    weeks.push({ key, days });
  }

  return weeks;
};

/** First and last day the grid shows, so the absences query can cover the padding. */
export const monthGridBounds = (
  anchor: CalendarDate,
): { readonly start: CalendarDate; readonly end: CalendarDate } => {
  const first = anchor.startOfMonth;
  const last = anchor.endOfMonth;
  return { start: first.addDays(-sundayIndex(first)), end: last.addDays(6 - sundayIndex(last)) };
};
